import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Badge, Card, Row, Col, Accordion, Table, Container } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { TriangleAlert as FaExclamationTriangle, Calendar as FaCalendarAlt } from 'lucide-react';
import QuestionnaireModal from '../../components/QuestionnaireModal';
import GradientButton from '../../components/GradientButton';
import { checkVacationAccess } from '../../actions/questionnaireActions';
import { getUserDetails } from '../../actions/userActions';
import axios from '../../api/axios';

function HomeScreen() {
    const [showQuestionnaire, setShowQuestionnaire] = useState(false);
    const [sessionDetails, setSessionDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    const userDetails = useSelector(state => state.userDetails);
    const { loading, user } = userDetails;

    const userLogin = useSelector(state => state.userLogin);
    const { userInfo } = userLogin;

    const questionnaireCheckAccess = useSelector(state => state.questionnaireCheckAccess);
    const { accessInfo } = questionnaireCheckAccess;

    const fadeInUp = {
        initial: { opacity: 0, y: 40 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.5 }
    };

    useEffect(() => {
        if (userInfo && (!user || !user.name)) {
            dispatch(getUserDetails('profile'));
        }
    }, [dispatch, userInfo, user]);

    useEffect(() => {
        if (!loading && !userInfo) {
            navigate('/login');
        }
    }, [userInfo, loading, navigate]);

    useEffect(() => {
        if (userInfo) {
            dispatch(checkVacationAccess());
        }
    }, [userInfo, dispatch]);

    const fetchSessionDetails = useCallback(async () => {
        setLoadingDetails(true);
        try {
            const config = {
                headers: {
                    Authorization: `Bearer ${userInfo.token}`
                }
            };
            const { data } = await axios.get('/questionnaire/current/', config);
            
            if (data.has_questionnaire && data.session?.status === 'completed') {
                setSessionDetails(data);
            }
        } catch (error) {
        } finally {
            setLoadingDetails(false);
        }
    }, [userInfo]);

    useEffect(() => {
        if (accessInfo && accessInfo.has_access && userInfo) {
            fetchSessionDetails();
        }
    }, [accessInfo, userInfo, fetchSessionDetails]);


    const openQuestionnaire = () => {
        setShowQuestionnaire(true);
    };

    const handleQuestionnaireComplete = () => {
        setShowQuestionnaire(false);
        dispatch(checkVacationAccess());
        fetchSessionDetails();
    };

    const formatAnswer = (questionKey, answerValue) => {
        
        if (!answerValue) {
            console.warn(`⚠️ No answerValue for ${questionKey}`);
            return '—';
        }
        
        if (typeof answerValue !== 'object') {
            console.warn(`⚠️ answerValue is not an object for ${questionKey}:`, typeof answerValue);
            return '—';
        }
        
        // Data urodzenia
        if (questionKey === 'birth_date') {
            if (answerValue.year && answerValue.month) {
                const monthName = new Date(2000, answerValue.month - 1).toLocaleDateString('pl-PL', { month: 'long' });
                const result = `${monthName} ${answerValue.year}`;
                return result;
            }
            console.warn(`⚠️ Invalid birth_date structure:`, answerValue);
            return '—';
        }
        
        // Klasa
        if (questionKey === 'current_class') {
            if (answerValue.value) {
                const result = `Klasa ${answerValue.value}`;
                return result;
            }
            console.warn(`⚠️ Invalid current_class structure:`, answerValue);
            return '—';
        }
        
        // Data rozpoczęcia praktyk
        if (questionKey === 'vocational_start_date') {
            if (answerValue.year && answerValue.month) {
                const monthName = new Date(2000, answerValue.month - 1).toLocaleDateString('pl-PL', { month: 'long' });
                const result = `${monthName} ${answerValue.year}`;
                return result;
            }
            console.warn(`⚠️ Invalid vocational_start_date structure:`, answerValue);
            return '—';
        }
        
        // Zmiana pracodawcy
        if (questionKey === 'changed_workplace') {
            if (answerValue.value === 'yes') {
                return 'Tak, zmieniłem pracodawcę';
            }
            if (answerValue.value === 'no') {
                return 'Nie, ten sam pracodawca';
            }
            console.warn(`⚠️ Invalid changed_workplace value:`, answerValue);
            return '—';
        }
        
        // Powtarzanie klasy
        if (questionKey === 'repeated_class') {
            if (answerValue.value === 'yes') {
                return 'Tak, powtarzam klasę';
            }
            if (answerValue.value === 'no') {
                return 'Nie, nie powtarzam';
            }
            console.warn(`⚠️ Invalid repeated_class value:`, answerValue);
            return '—';
        }
        
        // Wykorzystany urlop
        if (questionKey === 'vacation_used') {
            if (!answerValue.type) {
                console.warn(`⚠️ No type in vacation_used:`, answerValue);
                return '—';
            }
            if (answerValue.type === 'dont_remember') {
                return 'Nie pamiętam';
            }
            if (answerValue.type === 'exact') {
                const days = answerValue.value !== undefined && answerValue.value !== null ? answerValue.value : 0;
                return `${days} dni (dokładnie)`;
            }
            if (answerValue.type === 'approximate') {
                const days = answerValue.value !== undefined && answerValue.value !== null ? answerValue.value : 0;
                return `~${days} dni (około)`;
            }
            console.warn(`⚠️ Unknown vacation_used type:`, answerValue.type);
            return '—';
        }
        
        // Pozostały urlop z poprzedniego roku
        if (questionKey === 'vacation_remaining') {
            if (!answerValue.type) {
                console.warn(`⚠️ No type in vacation_remaining:`, answerValue);
                return '—';
            }
            if (answerValue.type === 'dont_remember') {
                return 'Nie pamiętam / Nie zostało';
            }
            if (answerValue.type === 'exact') {
                const days = answerValue.value !== undefined && answerValue.value !== null ? answerValue.value : 0;
                return `${days} dni (dokładnie)`;
            }
            if (answerValue.type === 'approximate') {
                const days = answerValue.value !== undefined && answerValue.value !== null ? answerValue.value : 0;
                return `~${days} dni (około)`;
            }
            console.warn(`⚠️ Unknown vacation_remaining type:`, answerValue.type);
            return '—';
        }
        
        // Domyślnie - dla nieznanych typów
        console.warn(`⚠️ Unknown question type: ${questionKey}`, answerValue);
        return JSON.stringify(answerValue);
    };

    const calculateVocationalStartAge = () => {
        if (!sessionDetails || !sessionDetails.current_answers) return '—';
        
        const birthAnswer = sessionDetails.current_answers['birth_date'];
        const vocationalAnswer = sessionDetails.current_answers['vocational_start_date'];
        
        if (!birthAnswer || !vocationalAnswer) return '—';
        
        const birthYear = birthAnswer.answer_value.year;
        const vocationalYear = vocationalAnswer.answer_value.year;
        const vocationalMonth = vocationalAnswer.answer_value.month;
        const birthMonth = birthAnswer.answer_value.month;
        
        let age = vocationalYear - birthYear;
        
        if (vocationalMonth < birthMonth) {
            age -= 1;
        }
        
        return age;
    };

    if (loading || !userInfo) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                        <span className="visually-hidden">Ładowanie...</span>
                    </div>
                    <p className="mt-3">Ładowanie...</p>
                </div>
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            paddingTop: '2rem',
            paddingBottom: '4rem',
            overflowX: 'clip',
            width: '95%'
        }}>
            <Container fluid className="px-2 px-md-3 scalable-column">
                
                {/* HERO - Powitanie */}
                <motion.div
                    {...fadeInUp}
                    className="text-center mb-5"
                >
                    <h1 className="display-4 fw-bold mb-3">
                        Witaj, {userInfo.name}!
                    </h1>
                    <p className="lead pub-muted">
                        Twój panel kontrolny SIGMA
                    </p>
                </motion.div>

                {accessInfo && (
                    <>
                        {/* STATUS KWESTIONARIUSZA */}
                        <motion.div {...fadeInUp} className="mb-5">
                            <Card 
                                className="border-0 shadow-lg"
                                style={{
                                    borderRadius: '30px',
                                    overflow: 'hidden',
                                    background: accessInfo.has_access 
                                        ? 'var(--pub-bg-alt)'
                                        : 'var(--pub-bg-alt)'
                                }}
                            >
                                <Card.Body className="p-5">
                                    <div className="d-flex align-items-center justify-content-between mb-4">
                                        <h2 className="fw-bold mb-0">
                                            {accessInfo.has_access ? (
                                                <>
                                                    Status kwestionariusza
                                                </>
                                            ) : (
                                                <>
                                                    <FaExclamationTriangle className="me-2" style={{ color: '#f5576c' }} />
                                                    Wymagana akcja
                                                </>
                                            )}
                                        </h2>
                                        <Badge 
                                            bg={accessInfo.has_access ? 'success' : 'danger'}
                                            className="px-4 py-2"
                                            style={{ fontSize: '1rem', borderRadius: '15px' }}
                                        >
                                            {accessInfo.has_access ? 'Ukończony' : 'Oczekujący'}
                                        </Badge>
                                    </div>

                                    {accessInfo.has_access ? (
                                        <>
                                            <p className="mb-4" style={{ fontSize: '1.1rem' }}>
                                                ✅ Kwestionariusz wypełniony. Twoja pula urlopowa:
                                            </p>
                                            
                                            <Row className="g-4">
                                                <Col xs={12} sm={6} lg={3}>
                                                    <Card 
                                                        className="text-center border-0 h-100"
                                                        style={{
                                                            background: 'var(--pub-bg-alt)',
                                                            borderRadius: '20px'
                                                        }}
                                                    >
                                                        <Card.Body className="p-4">
                                                            <h6 className="pub-muted mb-2">Przyznane w tym roku</h6>
                                                            <h2 className="mb-0 fw-bold">
                                                                {accessInfo.vacation_days || 0}
                                                            </h2>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                                <Col xs={12} sm={6} lg={3}>
                                                    <Card 
                                                        className="text-center border-0 h-100"
                                                        style={{
                                                            background: 'var(--pub-bg-alt)',
                                                            borderRadius: '20px'
                                                        }}
                                                    >
                                                        <Card.Body className="p-4">
                                                            <h6 className="pub-muted mb-2">Przeniesione</h6>
                                                            <h2 className="mb-0 fw-bold">
                                                                {accessInfo.carried_over_days || 0}
                                                            </h2>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                                <Col xs={12} sm={6} lg={3}>
                                                    <Card 
                                                        className="text-center border-0 h-100"
                                                        style={{
                                                            background: 'var(--pub-bg-alt)',
                                                            borderRadius: '20px'
                                                        }}
                                                    >
                                                        <Card.Body className="p-4">
                                                            <h6 className="pub-muted mb-2">Wykorzystane</h6>
                                                            <h2 className="mb-0 fw-bold">
                                                                {accessInfo.used_days || 0}
                                                            </h2>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                                <Col xs={12} sm={6} lg={3}>
                                                    <Card 
                                                        className="text-center border-0 h-100"
                                                        style={{
                                                            background: 'var(--pub-bg-alt)',
                                                            borderRadius: '20px'
                                                        }}
                                                    >
                                                        <Card.Body className="p-4">
                                                            <h6 className="pub-muted mb-2">Dostępne</h6>
                                                            <h2 className="mb-0 fw-bold">
                                                                {accessInfo.available_days || 0}
                                                            </h2>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            </Row>

                                            {(accessInfo.carried_over_days > 0 || accessInfo.used_days > 0) && (
                                                <div 
                                                    className="mt-4 p-4"
                                                    style={{
                                                        background: 'rgba(255,255,255,0.1)',
                                                        borderRadius: '20px',
                                                        backdropFilter: 'blur(10px)'
                                                    }}
                                                >
                                                    <small>
                                                        💡 {accessInfo.vacation_days} dni przyznanych w tym roku
                                                        {accessInfo.carried_over_days > 0 && (
                                                            <> + {accessInfo.carried_over_days} {accessInfo.carried_over_days === 1 ? 'dzień' : 'dni'} przeniesiony z poprzednich lat</>
                                                        )}
                                                        {accessInfo.used_days > 0 && (
                                                            <> - {accessInfo.used_days} {accessInfo.used_days === 1 ? 'dzień' : 'dni'} wykorzystany</>
                                                        )}
                                                        {' '}= <strong>{accessInfo.available_days} {accessInfo.available_days === 1 ? 'dzień' : 'dni'} dostępnych</strong>
                                                    </small>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className="text-center">
                                            <p className="mb-3" style={{ fontSize: '1.1rem' }}>
                                                Musisz wypełnić roczny kwestionariusz, aby uzyskać dostęp do puli urlopowej.
                                            </p>
                                            {accessInfo.deadline_date && (
                                                <p className="pub-muted mb-4">
                                                    <FaCalendarAlt className="me-2" />
                                                    Termin: {accessInfo.deadline_date}
                                                </p>
                                            )}
                                            <GradientButton
                                                onClick={openQuestionnaire}
                                                gradient= 'blue'
                                            >
                                                Wypełnij kwestionariusz
                                            </GradientButton>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </motion.div>

                        {/* ODPOWIEDZI Z KWESTIONARIUSZA */}
                        {accessInfo.has_access && sessionDetails && (
                            <motion.div {...fadeInUp} className="mb-5">
                                <Card 
                                    className="border-0 shadow-lg"
                                    style={{
                                        borderRadius: '30px',
                                        overflow: 'hidden',
                                        background: 'rgba(255,255,255,0.05)',
                                        backdropFilter: 'blur(10px)'
                                    }}
                                >
                                    <Card.Header 
                                        className="border-0 p-4"
                                        style={{
                                            background: 'var(--pub-bg-alt)'
                                        }}
                                    >
                                        <h3 className="fw-bold mb-0">
                                            Twoje odpowiedzi z kwestionariusza
                                        </h3>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        {loadingDetails ? (
                                            <div className="text-center py-5">
                                                <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                                                    <span className="visually-hidden">Ładowanie...</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="table-responsive">
                                                    <Table className="mb-0" style={{ color: 'white' }}>
                                                        <thead>
                                                            <tr>
                                                                <th style={{width: '50%', borderColor: 'rgba(255,255,255,0.1)'}}>Pytanie</th>
                                                                <th style={{width: '50%', borderColor: 'rgba(255,255,255,0.1)'}}>Odpowiedź</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {sessionDetails.questions.map((question) => {
                                                                const answer = sessionDetails.current_answers[question.question_key];
                                                                return (
                                                                    <tr key={question.question_key} className="align-middle">
                                                                    <td style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                                                                        <div className="d-flex align-items-start">
                                                                        <div>
                                                                            <strong>{question.question_text}</strong>
                                                                            {question.help_text && (
                                                                            <div><small className="pub-muted">{question.help_text}</small></div>
                                                                            )}
                                                                        </div>
                                                                        </div>
                                                                    </td>

                                                                    <td className="align-middle" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                                                                        <div className="d-flex align-items-center">
                                                                            <Badge 
                                                                                bg="primary" 
                                                                                className="me-2 px-3 py-2"
                                                                                style={{ fontSize: '0.9rem', borderRadius: '10px' }}
                                                                            >
                                                                                {answer ? formatAnswer(question.question_key, answer.answer_value) : '—'}
                                                                            </Badge>
                                                                        </div>
                                                                    </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </Table>
                                                </div>

                                                {/* SZCZEGÓŁY KALKULACJI */}
                                                {sessionDetails.session.calculation_data && (
                                                    <Accordion className="mt-4">
                                                        <Accordion.Item 
                                                            eventKey="0"
                                                            style={{
                                                                background: 'rgba(255,255,255,0.05)',
                                                                border: '1px solid rgba(255,255,255,0.1)',
                                                                borderRadius: '20px',
                                                                overflow: 'hidden'
                                                            }}
                                                        >
                                                            <Accordion.Header>
                                                                <strong>Szczegóły wyliczenia puli urlopowej</strong>
                                                            </Accordion.Header>
                                                            <Accordion.Body className="">
                                                                <div 
                                                                    className="p-4 mb-4"
                                                                    style={{
                                                                        background: 'rgba(33, 212, 253, 0.2)',
                                                                        borderRadius: '15px',
                                                                        border: '1px solid rgba(33, 212, 253, 0.3)'
                                                                    }}
                                                                >
                                                                    <p className="mb-2">
                                                                        <strong>Opis:</strong> {sessionDetails.session.calculation_data.opis}
                                                                    </p>
                                                                </div>
                                                                
                                                                <Row className="g-3 mb-4">
                                                                    <Col xs={6} md={3}>
                                                                        <div 
                                                                            className="p-3 text-center"
                                                                            style={{
                                                                                background: 'rgba(255,255,255,0.05)',
                                                                                borderRadius: '15px'
                                                                            }}
                                                                        >
                                                                            <small className="pub-muted d-block mb-2">Rok urodzenia</small>
                                                                            <strong style={{ fontSize: '1.2rem' }}>
                                                                                {sessionDetails.session.calculation_data?.rok_urodzenia || '—'}
                                                                            </strong>
                                                                        </div>
                                                                    </Col>
                                                                    <Col xs={6} md={3}>
                                                                        <div 
                                                                            className="p-3 text-center"
                                                                            style={{
                                                                                background: 'rgba(255,255,255,0.05)',
                                                                                borderRadius: '15px'
                                                                            }}
                                                                        >
                                                                            <small className="pub-muted d-block mb-2">Wiek na początku roku</small>
                                                                            <strong style={{ fontSize: '1.2rem' }}>
                                                                                {sessionDetails.session.calculation_data?.wiek_na_poczatku_roku ? `${sessionDetails.session.calculation_data.wiek_na_poczatku_roku} lat` : '—'}
                                                                            </strong>
                                                                        </div>
                                                                    </Col>
                                                                    <Col xs={6} md={3}>
                                                                        <div 
                                                                            className="p-3 text-center"
                                                                            style={{
                                                                                background: 'rgba(255,255,255,0.05)',
                                                                                borderRadius: '15px'
                                                                            }}
                                                                        >
                                                                            <small className="pub-muted d-block mb-2">Wiek rozpoczęcia nauki</small>
                                                                            <strong style={{ fontSize: '1.2rem' }}>
                                                                                {calculateVocationalStartAge()} lat
                                                                            </strong>
                                                                        </div>
                                                                    </Col>
                                                                    <Col xs={6} md={3}>
                                                                        <div 
                                                                            className="p-3 text-center"
                                                                            style={{
                                                                                background: 'rgba(255,255,255,0.05)',
                                                                                borderRadius: '15px'
                                                                            }}
                                                                        >
                                                                            <small className="pub-muted d-block mb-2">Skończy 18 lat</small>
                                                                            <strong style={{ fontSize: '1.2rem' }}>
                                                                                {sessionDetails.session.calculation_data.skonczy_18_lat_w_tym_roku ? 'Tak' : 'Nie'}
                                                                            </strong>
                                                                        </div>
                                                                    </Col>
                                                                </Row>

                                                                {sessionDetails.session.calculation_data.dni_startowe && (
                                                                    <p className="mb-2">
                                                                        <strong>Dni startowe (Klasa I):</strong> {sessionDetails.session.calculation_data.dni_startowe} dni
                                                                    </p>
                                                                )}

                                                                {sessionDetails.session.calculation_data.dni_roczne && (
                                                                    <p className="mb-2">
                                                                        <strong>Dni roczne:</strong> {sessionDetails.session.calculation_data.dni_roczne} dni
                                                                    </p>
                                                                )}

                                                                {sessionDetails.session.calculation_data.pelny_wymiar_roczny && (
                                                                    <>
                                                                        <p className="mb-2">
                                                                            <strong>Pełny wymiar roczny:</strong> {sessionDetails.session.calculation_data.pelny_wymiar_roczny} dni
                                                                        </p>
                                                                        <p className="mb-4">
                                                                            <strong>Dni proporcjonalne (8/12 roku):</strong> {sessionDetails.session.calculation_data.dni_proporcjonalne} dni
                                                                        </p>
                                                                    </>
                                                                )}

                                                                <div 
                                                                    className="p-4 text-center"
                                                                    style={{
                                                                        background: 'var(--pub-bg-alt)',
                                                                        borderRadius: '20px'
                                                                    }}
                                                                >
                                                                    <h4 className="fw-bold mb-0">
                                                                        Suma końcowa: {accessInfo.available_days} dni
                                                                    </h4>
                                                                </div>

                                                                {sessionDetails.session.calculation_data.uwaga && (
                                                                    <div 
                                                                        className="mt-3 p-3"
                                                                        style={{
                                                                            background: 'rgba(255, 193, 7, 0.2)',
                                                                            borderRadius: '15px',
                                                                            border: '1px solid rgba(255, 193, 7, 0.3)'
                                                                        }}
                                                                    >
                                                                        <small>Uwaga: {sessionDetails.session.calculation_data.uwaga}</small>
                                                                    </div>
                                                                )}
                                                            </Accordion.Body>
                                                        </Accordion.Item>
                                                    </Accordion>
                                                )}

                                                <div className="pub-muted mt-4 text-center">
                                                    <small>
                                                        Kwestionariusz wypełniony: {new Date(sessionDetails.session.completed_at).toLocaleString('pl-PL')}
                                                    </small>
                                                </div>
                                            </>
                                        )}
                                    </Card.Body>
                                </Card>
                            </motion.div>
                        )}
{/* 
                        PANEL TESTOWY
                        <motion.div {...fadeInUp}>
                            <Card 
                                className="border-0 shadow-lg"
                                style={{
                                    borderRadius: '30px',
                                    overflow: 'hidden',
                                    background: 'var(--pub-bg-alt)',
                                    border: '2px solid rgba(245, 87, 108, 0.3)'
                                }}
                            >
                                <Card.Header 
                                    className="border-0 p-4"
                                    style={{
                                        background: 'var(--pub-bg-alt)'
                                    }}
                                >
                                    <h3 className="fw-bold mb-0">
                                        Panel testowy - Development
                                    </h3>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <p className="pub-muted mb-4">
                                        Te przyciski służą do testowania funkcjonalności kwestionariusza. 
                                        Usuń ten panel przed wdrożeniem produkcyjnym!
                                    </p>
                                    
                                    <Row className="g-3 mb-4">
                                        <Col xs={12} md={6} lg={3}>
                                            <Button 
                                                variant="warning" 
                                                onClick={createSampleQuestions}
                                                className="w-100 py-3"
                                                style={{
                                                    borderRadius: '15px',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                Utwórz pytania testowe
                                            </Button>
                                        </Col>

                                        <Col xs={12} md={6} lg={3}>
                                            <Button 
                                                variant="primary" 
                                                onClick={simulateQuestionnaireActivation}
                                                disabled={isSimulating}
                                                className="w-100 py-3"
                                                style={{
                                                    borderRadius: '15px',
                                                    fontWeight: 'bold',
                                                    background: 'var(--pub-bg-alt)',
                                                    border: 'none'
                                                }}
                                            >
                                                {isSimulating ? 'Symulowanie...' : 'Symuluj aktywację'}
                                            </Button>
                                        </Col>
                                        
                                        <Col xs={12} md={6} lg={3}>
                                            <Button 
                                                variant="info" 
                                                onClick={forceShowQuestionnaire}
                                                className="w-100 py-3"
                                                style={{
                                                    borderRadius: '15px',
                                                    fontWeight: 'bold',
                                                    background: 'var(--pub-bg-alt)',
                                                    border: 'none'
                                                }}
                                            >
                                                Wymuś kwestionariusz
                                            </Button>
                                        </Col>

                                        <Col xs={12} md={6} lg={3}>
                                            <Button 
                                                variant="success" 
                                                onClick={() => {
                                                    dispatch(checkVacationAccess());
                                                    fetchSessionDetails();
                                                }}
                                                className="w-100 py-3"
                                                style={{
                                                    borderRadius: '15px',
                                                    fontWeight: 'bold',
                                                    background: 'var(--pub-bg-alt)',
                                                    border: 'none'
                                                }}
                                            >
                                                <FaSyncAlt className="me-2" />
                                                Odśwież status
                                            </Button>
                                        </Col>
                                    </Row>

                                    <div 
                                        className="p-4"
                                        style={{
                                            background: 'rgba(255,255,255,0.05)',
                                            borderRadius: '20px',
                                            backdropFilter: 'blur(10px)'
                                        }}
                                    >
                                        <h5 className="fw-bold mb-3">Instrukcja testowania:</h5>
                                        <ol className="mb-0" style={{ lineHeight: '2' }}>
                                            <li><strong>Krok 1:</strong> Kliknij "Utwórz pytania testowe" (tylko raz)</li>
                                            <li><strong>Krok 2:</strong> Kliknij "Symuluj aktywację kwestionariusza"</li>
                                            <li><strong>Krok 3:</strong> Wypełnij pytania w modalu</li>
                                            <li><strong>Krok 4:</strong> Kliknij "Wyślij kwestionariusz"</li>
                                            <li><strong>Krok 5:</strong> Sprawdź pulę urlopową i szczegóły odpowiedzi</li>
                                        </ol>
                                    </div>
                                </Card.Body>
                            </Card>
                        </motion.div> */}

                        <QuestionnaireModal 
                            show={showQuestionnaire}
                            onHide={() => setShowQuestionnaire(false)}
                            onComplete={handleQuestionnaireComplete}
                        />
                    </>
                )}

            </Container>
        </div>
    );
}

export default HomeScreen;